/**
 * Shared ImportQuestionsDialog — used by both admin-console and teacher-app.
 *
 * Admin-only features are activated via optional props:
 *   - `teacherOptions` → shows "On behalf of" dropdown
 *   - `supportsDryRun` → runs a dry-run pass, shows review phase with diffs
 *   - `acceptFileTypes` → ".json,.zip" for admin, ".json" for teacher
 *
 * Caller provides `parseFile` and `importFn` callbacks to decouple
 * from app-specific API clients and file-parsing logic (e.g. ZIP support).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@shared-ui/components';
import { JsonDiffViewer } from '@shared-ui/components/JsonDiffViewer';
import { PROGRAMME_OPTIONS, GRADE_OPTIONS } from '@padcanvas/shared';
import type { QuestionDiffItem, QuestionDiffResult } from '@padcanvas/shared';
import type { ImportResult } from './importApi';
import { reduceImagesForQuestions } from './selectiveImport';
import { WebLogger, WebComponentCode } from '../logging';

// ── Diagnostics ───────────────────────────────────────────────────────
// Import/preview requests can fail at the transport level (the browser's bare
// "Load failed" / "Failed to fetch", or an out-of-memory RangeError on large
// payloads). To tell those apart we log the exact error (name/message/stack),
// the request size, timing, and a memory/UA snapshot on every preview/import
// call — shipped to the backend tracingLogs via /console/client-logs.
const importLogger = new WebLogger(WebComponentCode.WEB_API, 'ImportQuestionsDialog');

/** Cheap payload stats for an ImportFile (avoids re-stringifying the whole body). */
function payloadStats(file: { questions: unknown[]; images?: Record<string, { data: string }> }) {
  let imageCount = 0;
  let imageBase64Bytes = 0;
  if (file.images) {
    for (const k in file.images) { imageCount++; imageBase64Bytes += file.images[k]?.data?.length || 0; }
  }
  return { questionCount: file.questions.length, imageCount, imageBase64Bytes };
}

/** Browser memory + UA snapshot — distinguishes a memory failure from a network one. */
function clientEnvSnapshot(): Record<string, unknown> {
  const perfMem = (typeof performance !== 'undefined' ? (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory : undefined);
  const nav = (typeof navigator !== 'undefined' ? navigator : undefined) as (Navigator & { deviceMemory?: number }) | undefined;
  return {
    userAgent: nav?.userAgent,
    deviceMemoryGB: nav?.deviceMemory,
    ...(perfMem ? {
      usedJSHeapMB: Math.round(perfMem.usedJSHeapSize / 1048576),
      totalJSHeapMB: Math.round(perfMem.totalJSHeapSize / 1048576),
      jsHeapLimitMB: Math.round(perfMem.jsHeapSizeLimit / 1048576),
    } : { perfMemoryUnavailable: true }),
  };
}

function errorDetail(err: unknown): Record<string, unknown> {
  if (err instanceof Error) return { errorName: err.name, errorMessage: err.message, errorStack: err.stack?.slice(0, 600) };
  return { errorName: 'non-error', errorMessage: String(err) };
}

// ── Types ─────────────────────────────────────────────────────────────

type Phase = 'configure' | 'running' | 'review' | 'results';

type ReviewTab = 'errors' | 'conflicts' | 'updates' | 'creates' | 'skipped' | 'unchanged';

export interface ImportFile {
  name: string;
  questions: Record<string, unknown>[];
  images?: Record<string, { data: string; mimeType: string }>;
}

// `ImportResult` lives in `./importApi.ts` — it's the unified shape the
// admin and teacher clients return after normalizing the backend's
// `QuestionImportResult` / `QuestionDiffResult` responses.

export interface TeacherOption {
  id: string;
  name: string;
}

interface Totals {
  created: number;
  updated: number;
  skipped: number;
  unchanged: number;
  failed: number;
  imported: number;
  batchId?: string;
  createdQuestionIds: string[];
  /** Question IDs grouped by their importBatchId (for the results panel). */
  questionsByBatchId: Record<string, string[]>;
  errors: string[];
  warnings: string[];
}

const EMPTY_TOTALS: Totals = {
  created: 0, updated: 0, skipped: 0, unchanged: 0, failed: 0, imported: 0,
  createdQuestionIds: [], questionsByBatchId: {}, errors: [], warnings: [],
};

/** Opts shared by both endpoints. Identical wire payload; the URL is the only
 *  thing that differs between the preview and real-run calls. */
export interface ImportFnOpts {
  forceUpdate?: boolean;
  importBatchId?: string;
  onBehalfOfTeacherId?: string;
  tag?: string;
  selectedIds?: string[];
  retainImportBatchId?: boolean;
}

export interface ImportQuestionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImported?: (importBatchId?: string) => void;

  /** Parse a user-selected file into ImportFile[]. Caller handles ZIP vs JSON. */
  parseFile: (file: File) => Promise<ImportFile[]>;
  /** Execute the real-run import call (POST /console/questions/import).
   *  Always writes — caller wires to adminApi or teacherApi. */
  importFn: (
    questions: Record<string, unknown>[],
    images: ImportFile['images'],
    opts: ImportFnOpts,
  ) => Promise<ImportResult>;
  /** Execute the dry-run preview call (POST /console/questions/import/preview).
   *  Required when `supportsDryRun` is true. Never writes. */
  previewFn?: (
    questions: Record<string, unknown>[],
    images: ImportFile['images'],
    opts: ImportFnOpts,
  ) => Promise<QuestionDiffResult>;

  /** File input accept attribute. Default: ".json" */
  acceptFileTypes?: string;
  /** If provided, shows "On behalf of" teacher dropdown (admin only). */
  teacherOptions?: TeacherOption[];
  /** If true, runs a dry-run pass and shows review phase with diffs. Requires `previewFn`. */
  supportsDryRun?: boolean;
  /** Message shown below the tag field, e.g. "Questions will be imported as your private questions." */
  infoMessage?: string;
  /** If provided, enables "Create Assignment" option after import on behalf of a teacher. */
  onCreateAssignment?: (teacherId: string, title: string, questionIds: string[]) => Promise<void>;
}

// ── Constants ────────────────────────────────────────────────────────

const QUESTION_DIFF_IGNORE = new Set([
  '_rid', '_self', '_etag', '_attachments', '_ts',
  'updatedAt', 'updatedBy', 'createdAt', 'createdBy',
  'importBatchId',
]);

// ── Helpers ───────────────────────────────────────────────────────────

interface ExamAppearance {
  exam: string;
  date?: string;
  session?: string;
  questionNo?: string;
  marks?: number;
}

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * Apply override fields to each question. Only fields that were actually
 * edited (changed from the parsed default) are applied — untouched fields
 * leave per-question values intact.
 */
function applyOverrides(
  questions: Record<string, unknown>[],
  overrides: { exam?: string; date?: string; session?: string; targetExams?: string[]; targetClasses?: string[] },
): Record<string, unknown>[] {
  const hasExamOverrides = !!(overrides.exam || overrides.date || overrides.session);
  const hasTargetExams = overrides.targetExams && overrides.targetExams.length > 0;
  const hasTargetClasses = overrides.targetClasses && overrides.targetClasses.length > 0;
  if (!hasExamOverrides && !hasTargetExams && !hasTargetClasses) return questions;
  return questions.map(q => {
    let result = { ...q };
    if (hasExamOverrides) {
      const appearances = Array.isArray(q.appearedInExams) ? q.appearedInExams as ExamAppearance[] : [];
      const existing = appearances[0];
      const merged: ExamAppearance = {
        exam: overrides.exam ?? existing?.exam ?? '',
        date: overrides.date ?? existing?.date,
        session: overrides.session ?? existing?.session,
        questionNo: existing?.questionNo,
        marks: existing?.marks,
      };
      if (existing || merged.exam || merged.date) {
        result = { ...result, appearedInExams: [merged, ...appearances.slice(1)] };
      }
    }
    if (hasTargetExams) result.targetExams = overrides.targetExams;
    if (hasTargetClasses) result.class = overrides.targetClasses;
    return result;
  });
}

// ── Run import across files ───────────────────────────────────────────

/** Real import across one or more files; accumulates totals and surfaces errors.
 *  Each call writes to the database. */
async function runImportFiles(
  files: ImportFile[],
  importFn: ImportQuestionsDialogProps['importFn'],
  opts: ImportFnOpts,
  onProgress: (current: number, total: number, fileName: string) => void,
): Promise<Totals> {
  let totals: Totals = { ...EMPTY_TOTALS };
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onProgress(i + 1, files.length, file.name);
    const stats = payloadStats(file);
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    importLogger.info('Import POST starting', { file: file.name, fileIndex: i, fileCount: files.length, ...stats, ...clientEnvSnapshot() });
    try {
      const result = await importFn(file.questions, file.images, opts);
      importLogger.info('Import POST ok', { file: file.name, ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0), imported: result.imported, ...stats });

      const errors: string[] = [];
      for (const qr of result.questions) {
        if (qr.status === 'failed') errors.push(`Question ${qr.id}: ${qr.reason || 'unknown error'}`);
      }
      const newCreatedIds = result.questions
        .filter(qr => qr.status === 'created')
        .map(qr => qr.id);
      const batchGroups = { ...totals.questionsByBatchId };
      for (const qr of result.questions) {
        if ((qr.status === 'created' || qr.status === 'updated') && qr.importBatchId) {
          if (!batchGroups[qr.importBatchId]) batchGroups[qr.importBatchId] = [];
          batchGroups[qr.importBatchId].push(qr.id);
        }
      }
      totals = {
        created: totals.created + result.created,
        updated: totals.updated + result.updated,
        skipped: totals.skipped + result.skipped,
        unchanged: totals.unchanged + (result.unchanged || 0),
        failed: totals.failed + (result.failed || 0),
        imported: totals.imported + result.imported,
        batchId: result.importBatchId ?? totals.batchId,
        createdQuestionIds: [...totals.createdQuestionIds, ...newCreatedIds],
        questionsByBatchId: batchGroups,
        errors: [...totals.errors, ...(files.length > 1 ? errors.map(e => `[${file.name}] ${e}`) : errors)],
        warnings: [...totals.warnings, ...(files.length > 1 ? (result.warnings || []).map(w => `[${file.name}] ${w}`) : (result.warnings || []))],
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      importLogger.error('Import POST failed', err instanceof Error ? err : new Error(msg), {
        file: file.name, phase: 'import',
        ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0),
        ...stats, ...errorDetail(err), ...clientEnvSnapshot(),
      });
      totals.errors.push(files.length > 1 ? `[${file.name}] ${msg}` : msg);
    }
  }
  return totals;
}

/** Dry-run preview across one or more files. Returns the first file's
 *  `QuestionDiffResult` (the dialog expects a single file in the dry-run path)
 *  or a synthetic error-only Totals object if the call threw. */
async function runPreviewFiles(
  files: ImportFile[],
  previewFn: NonNullable<ImportQuestionsDialogProps['previewFn']>,
  opts: ImportFnOpts,
  onProgress: (current: number, total: number, fileName: string) => void,
): Promise<{ diffResult?: QuestionDiffResult; errors: string[]; batchId?: string }> {
  const collectedErrors: string[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onProgress(i + 1, files.length, file.name);
    const stats = payloadStats(file);
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    importLogger.info('Preview POST starting', { file: file.name, ...stats, imagesSent: false, ...clientEnvSnapshot() });
    try {
      // Opt 1: the dry-run diffs on image HASHES (already on the questions from
      // export), never on bytes — so omit the (large) images map entirely. The
      // review UI still renders previews from the local `file.images` in memory.
      const diff = await previewFn(file.questions, undefined, opts);
      importLogger.info('Preview POST ok', { file: file.name, ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0), ...stats });
      // Single-file dry-run is the contract — bail out with the first result.
      return { diffResult: diff, batchId: diff.importBatchId, errors: [] };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      importLogger.error('Preview POST failed', err instanceof Error ? err : new Error(msg), {
        file: file.name, phase: 'preview',
        ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0),
        ...stats, ...errorDetail(err), ...clientEnvSnapshot(),
      });
      collectedErrors.push(files.length > 1 ? `[${file.name}] ${msg}` : msg);
    }
  }
  return { errors: collectedErrors };
}

// ── Component ─────────────────────────────────────────────────────────

export function ImportQuestionsDialog({
  isOpen, onClose, onImported,
  parseFile, importFn, previewFn,
  acceptFileTypes = '.json',
  teacherOptions,
  supportsDryRun = false,
  infoMessage,
  onCreateAssignment,
}: ImportQuestionsDialogProps) {

  const [phase, setPhase] = useState<Phase>('configure');
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ImportFile[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);

  // Exam/Date/Session — initial values from parse; current values are edits.
  const [initialExam, setInitialExam] = useState('');
  const [initialDate, setInitialDate] = useState('');
  const [initialSession, setInitialSession] = useState('');
  const [exam, setExam] = useState('');
  const [date, setDate] = useState('');
  const [session, setSession] = useState('');

  // Target exams & classes
  const [initialTargetExams, setInitialTargetExams] = useState<string[]>([]);
  const [initialTargetClasses, setInitialTargetClasses] = useState<string[]>([]);
  const [targetExams, setTargetExams] = useState<string[]>([]);
  const [targetClasses, setTargetClasses] = useState<string[]>([]);

  // Tracks which fields have mixed (non-uniform) values across questions in the file
  const [mixed, setMixed] = useState<{ exam: boolean; date: boolean; session: boolean; targetExams: boolean; targetClasses: boolean }>({ exam: false, date: false, session: false, targetExams: false, targetClasses: false });

  const [onBehalfOf, setOnBehalfOf] = useState('');
  const [importTag, setImportTag] = useState('');
  const [retainImportBatchId, setRetainImportBatchId] = useState(true);

  // Run state
  const [progress, setProgress] = useState<{ current: number; total: number; fileName: string; step: string } | null>(null);
  const [totals, setTotals] = useState<Totals>(EMPTY_TOTALS);
  const [dryRunBatchId, setDryRunBatchId] = useState<string | undefined>();

  // Review phase state
  const [diffResult, setDiffResult] = useState<QuestionDiffResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<ReviewTab>('updates');
  const [updatesPivot, setUpdatesPivot] = useState<'question' | 'field'>('question');
  // True once the admin has explicitly acknowledged any `[loud]`-tagged
  // warnings on the dry-run (e.g. reparenting questions from an existing
  // teacher). Reset whenever a fresh dry-run lands.
  const [loudConfirmed, setLoudConfirmed] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset on re-open
  useEffect(() => {
    if (!isOpen) return;
    setPhase('configure');
    setFile(null);
    setParsed(null);
    setParseError(null);
    setParsing(false);
    setInitialExam(''); setInitialDate(''); setInitialSession('');
    setExam(''); setDate(''); setSession('');
    setInitialTargetExams([]); setInitialTargetClasses([]);
    setTargetExams([]); setTargetClasses([]);
    setMixed({ exam: false, date: false, session: false, targetExams: false, targetClasses: false });
    setOnBehalfOf('');
    setImportTag('');
    setRetainImportBatchId(true);
    setProgress(null);
    setTotals(EMPTY_TOTALS);
    setDryRunBatchId(undefined);
    setDiffResult(null);
    setSelectedIds(new Set());
    setExpandedIds(new Set());
    setActiveTab('updates');
    setLoudConfirmed(false);
  }, [isOpen]);

  // ── Configure: pick file ──────────────────────────────────────────

  const onFilePicked = async (picked: File) => {
    setFile(picked);
    setParsed(null);
    setParseError(null);
    setParsing(true);
    try {
      const files = await parseFile(picked);
      // Ensure every question carries an id. A human-authored (id-less) JSON
      // otherwise imports nothing: the review→confirm step ships only questions
      // whose id is in `selectedIds` (the preview-computed ids), but an id-less
      // file leaves `q.id` undefined so all get filtered out at confirm time.
      // A stable client temp id flows through preview + selection; the server
      // still rewrites it to the final (tq-/uuid5) id on create.
      const tmpId = () =>
        globalThis.crypto?.randomUUID?.() ?? `import-tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      for (const f of files) {
        for (const q of f.questions) {
          if (!(q as { id?: string }).id) (q as { id?: string }).id = tmpId();
        }
      }
      setParsed(files);

      // Gather all questions across all files.
      const allQuestions = files.flatMap(f => f.questions);

      // Extract per-question values
      const getExamField = (q: Record<string, unknown>) => {
        const apps = Array.isArray(q.appearedInExams) ? q.appearedInExams as ExamAppearance[] : [];
        return apps[0];
      };
      const getTargetExams = (q: Record<string, unknown>): string[] =>
        Array.isArray(q.targetExams) ? q.targetExams as string[] : [];
      const getTargetClasses = (q: Record<string, unknown>): string[] =>
        Array.isArray(q.class) ? q.class as string[] : typeof q.class === 'string' ? [q.class] : [];

      const firstApp = getExamField(allQuestions[0] || {});
      const firstExam = firstApp?.exam || '';
      const firstDate = firstApp?.date || '';
      const firstSession = firstApp?.session || '';
      const firstTargetExams = getTargetExams(allQuestions[0] || {});
      const firstTargetClasses = getTargetClasses(allQuestions[0] || {});

      // Check if all questions agree
      let mixedExam = false, mixedDate = false, mixedSession = false;
      let mixedTE = false, mixedTC = false;
      for (let i = 1; i < allQuestions.length; i++) {
        const app = getExamField(allQuestions[i]);
        if ((app?.exam || '') !== firstExam) mixedExam = true;
        if ((app?.date || '') !== firstDate) mixedDate = true;
        if ((app?.session || '') !== firstSession) mixedSession = true;
        if (!arraysEqual(getTargetExams(allQuestions[i]), firstTargetExams)) mixedTE = true;
        if (!arraysEqual(getTargetClasses(allQuestions[i]), firstTargetClasses)) mixedTC = true;
      }

      setMixed({ exam: mixedExam, date: mixedDate, session: mixedSession, targetExams: mixedTE, targetClasses: mixedTC });

      // Pre-fill only if all questions agree; otherwise leave empty.
      const iExam = mixedExam ? '' : firstExam;
      const iDate = mixedDate ? '' : firstDate;
      const iSession = mixedSession ? '' : firstSession;
      const iTargetExams = mixedTE ? [] : firstTargetExams;
      const iTargetClasses = mixedTC ? [] : firstTargetClasses;

      setInitialExam(iExam); setInitialDate(iDate); setInitialSession(iSession);
      setExam(iExam); setDate(iDate); setSession(iSession);
      setInitialTargetExams(iTargetExams); setInitialTargetClasses(iTargetClasses);
      setTargetExams(iTargetExams); setTargetClasses(iTargetClasses);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
    } finally {
      setParsing(false);
    }
  };

  const totalQuestions = useMemo(() => (parsed || []).reduce((n, f) => n + f.questions.length, 0), [parsed]);

  const overrides = useMemo(() => ({
    exam: exam !== initialExam ? exam : undefined,
    date: date !== initialDate ? date : undefined,
    session: session !== initialSession ? session : undefined,
    targetExams: !arraysEqual(targetExams, initialTargetExams) ? targetExams : undefined,
    targetClasses: !arraysEqual(targetClasses, initialTargetClasses) ? targetClasses : undefined,
  }), [exam, date, session, initialExam, initialDate, initialSession, targetExams, targetClasses, initialTargetExams, initialTargetClasses]);

  // ── Start import ──────────────────────────────────────────────────

  const buildFilesWithOverrides = () => (parsed || []).map(f => ({
    ...f,
    questions: applyOverrides(f.questions, overrides),
  }));

  const start = async () => {
    if (!parsed) return;
    setPhase('running');
    setTotals(EMPTY_TOTALS);

    const filesWithOverrides = buildFilesWithOverrides();

    if (supportsDryRun && previewFn) {
      setProgress({ current: 0, total: parsed.length, fileName: '', step: 'Checking for existing questions\u2026' });
      const dryRun = await runPreviewFiles(
        filesWithOverrides, previewFn,
        { onBehalfOfTeacherId: onBehalfOf || undefined },
        (current, total, fileName) => setProgress({ current, total, fileName, step: 'Checking for existing questions\u2026' }),
      );

      if (dryRun.errors.length > 0) {
        setTotals({ ...EMPTY_TOTALS, errors: dryRun.errors });
        setPhase('results');
        return;
      }

      if (dryRun.diffResult) {
        // Show review phase with per-question diffs
        const dr = dryRun.diffResult;
        setDiffResult(dr);
        setDryRunBatchId(dr.importBatchId);
        // Fresh dry-run → require re-acknowledgement of any loud warnings.
        setLoudConfirmed(false);

        // Pre-select all creates + updates; unchanged and skipped are unchecked
        // (unchanged items would be no-op writes; skipped items are excluded by
        // policy and require an explicit force-update toggle).
        const preSelected = new Set<string>();
        for (const item of dr.creates) preSelected.add(item.id);
        for (const item of dr.updates) preSelected.add(item.id);
        setSelectedIds(preSelected);
        setExpandedIds(new Set());

        // Default to errors when any exist (surfaces blockers first), then
        // conflicts (need a human decision); else fall through to the
        // highest-signal bucket.
        if ((dr.errors ?? []).length > 0) setActiveTab('errors');
        else if ((dr.conflicts ?? []).length > 0) setActiveTab('conflicts');
        else if (dr.updates.length > 0) setActiveTab('updates');
        else if (dr.creates.length > 0) setActiveTab('creates');
        else if ((dr.unchanged ?? []).length > 0) setActiveTab('unchanged');
        else setActiveTab('skipped');

        setPhase('review');
        return;
      }

      // Fallback: previewFn returned no diff and no errors (shouldn't happen).
      // Proceed straight to a real import so the user isn't stranded.
      setDryRunBatchId(dryRun.batchId);
      await doRealImport(filesWithOverrides, { forceUpdate: false, importBatchId: dryRun.batchId, tag: importTag.trim() || undefined, retainImportBatchId });
    } else {
      // No dry run — import directly.
      await doRealImport(filesWithOverrides, { tag: importTag.trim() || undefined, retainImportBatchId });
    }
  };

  const doRealImport = async (
    files: ImportFile[],
    opts: {
      forceUpdate?: boolean;
      importBatchId?: string;
      tag?: string;
      selectedIds?: string[];
      retainImportBatchId?: boolean;
      /** Errors already identified in the dry-run. The client filters these
       *  out of `files` before calling the real import (so the server doesn't
       *  redo validation), but we still want them in the final results totals
       *  and error list the admin sees. */
      preCountedErrors?: QuestionDiffItem[];
      /** Dry-run counts for questions we deliberately did NOT send to the server
       *  (only selected create/update questions are shipped — see onReviewImport).
       *  Folded into the results summary so it stays accurate. */
      preCountedUnchanged?: number;
      preCountedSkipped?: number;
    },
  ) => {
    setPhase('running');
    const step = opts.forceUpdate ? 'Importing (force update)\u2026' : 'Importing questions\u2026';
    setProgress({ current: 0, total: files.length, fileName: '', step });

    const final = await runImportFiles(
      files, importFn,
      { forceUpdate: opts.forceUpdate, importBatchId: opts.importBatchId, onBehalfOfTeacherId: onBehalfOf || undefined, tag: opts.tag, selectedIds: opts.selectedIds, retainImportBatchId: opts.retainImportBatchId },
      (current, total, fileName) => setProgress({ current, total, fileName, step }),
    );

    // Fold dry-run errors into the results so the admin sees them even
    // though we didn't ship those questions to the server.
    if (opts.preCountedErrors && opts.preCountedErrors.length > 0) {
      final.failed += opts.preCountedErrors.length;
      for (const e of opts.preCountedErrors) {
        final.errors.push(`${e.label}: ${e.skipReason ?? 'validation error'}`);
      }
    }
    // Questions we didn't ship (unchanged + anything not selected) still count
    // toward the summary the admin sees.
    if (opts.preCountedUnchanged) final.unchanged += opts.preCountedUnchanged;
    if (opts.preCountedSkipped) final.skipped += opts.preCountedSkipped;

    setTotals(final);
    setPhase('results');

    if (final.imported > 0) {
      onImported?.(final.batchId);
    }
  };

  const onReviewImport = () => {
    if (!parsed || !diffResult) return;
    const filesWithOverrides = buildFilesWithOverrides();
    // Opt 2: ship ONLY the selected questions (creates + updates + any
    // force-selected skipped) and ONLY the images they reference — not the
    // whole file. `selectedIds` already excludes dry-run errors (never
    // pre-selected) and unchanged/skipped (unchecked by default). This is the
    // big payload win for the real import.
    const selectedFiles = filesWithOverrides.map(f => {
      const questions = f.questions.filter(q => selectedIds.has((q as { id?: string }).id ?? ''));
      return { ...f, questions, images: reduceImagesForQuestions(questions, f.images) };
    });
    // Determine if any skipped (published) items were selected — need forceUpdate
    const skippedIds = new Set(diffResult.skipped.map(s => s.id));
    const hasSelectedSkipped = [...selectedIds].some(id => skippedIds.has(id));
    // Counts for everything we are NOT sending, so the summary stays accurate.
    const preCountedUnchanged = (diffResult.unchanged ?? []).length;
    const preCountedSkipped = [
      ...diffResult.creates, ...diffResult.updates, ...diffResult.skipped, ...(diffResult.conflicts ?? []),
    ].filter(i => !selectedIds.has(i.id)).length;
    void doRealImport(selectedFiles, {
      forceUpdate: hasSelectedSkipped,
      importBatchId: dryRunBatchId,
      tag: importTag.trim() || undefined,
      selectedIds: [...selectedIds],
      preCountedErrors: diffResult.errors ?? [],
      preCountedUnchanged,
      preCountedSkipped,
      retainImportBatchId,
    });
  };

  // ── Review phase helpers ──────────────────────────────────────────

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllInTab = (items: QuestionDiffItem[]) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = items.every(i => next.has(i.id));
      for (const item of items) {
        if (allSelected) next.delete(item.id);
        else next.add(item.id);
      }
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  // ── Render ────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Questions"
      size={phase === 'review' ? 'xl' : 'lg'}
      closeOnOverlayClick={phase === 'configure' || phase === 'results'}
    >
      {phase === 'configure' && (() => {
        const inputStyle: React.CSSProperties = { padding: '8px 10px', borderRadius: 6, fontSize: 13, height: 40, border: '1px solid var(--border-color-dark)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
        const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-secondary)' };
        return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* File picker — div (not label) to avoid double-trigger */}
          <div style={labelStyle}>
            <span>File</span>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', height: 'auto', minHeight: 36 }}
            >
              {file ? (
                <>
                  <span style={{ color: 'var(--text-primary)' }}>{file.name}</span>
                  {parsing && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Parsing…</span>}
                  {parsed && !parsing && (
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                      — {totalQuestions} question{totalQuestions === 1 ? '' : 's'}{parsed.length > 1 ? ` in ${parsed.length} files` : ''}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ color: 'var(--text-tertiary)' }}>Click to select a {acceptFileTypes.replace(/\./g, '').replace(/,/g, ' or ')} file…</span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptFileTypes}
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFilePicked(f); e.target.value = ''; }}
            />
            {parseError && <div className="status-error" style={{ marginTop: 4 }}>Failed to parse file: {parseError}</div>}
          </div>

          {/* Exam / Date / Session */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>
              Exam
              <select value={exam} onChange={(e) => setExam(e.target.value)} disabled={!parsed} style={inputStyle}>
                <option value="">— No Change —</option>
                {PROGRAMME_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </label>
            <label style={labelStyle}>
              Date
              <input
                type={date ? 'date' : 'text'}
                value={date}
                placeholder="No Change"
                onFocus={(e) => { if (!date) e.currentTarget.type = 'date'; }}
                onBlur={(e) => { if (!date) e.currentTarget.type = 'text'; }}
                onChange={(e) => setDate(e.target.value)}
                disabled={!parsed}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Session
              <input type="text" value={session} onChange={(e) => setSession(e.target.value)} placeholder="No Change" disabled={!parsed} style={inputStyle} />
            </label>
          </div>
          {parsed && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: -6 }}>
              {(mixed.exam || mixed.date || mixed.session)
                ? <>Questions have different values for {[mixed.exam && 'exam', mixed.date && 'date', mixed.session && 'session'].filter(Boolean).join(', ')}. Set a value to override all, or leave as "No Change" to keep per-question values.</>
                : <>Pre-filled from file (all questions match). Edit a field to override all — untouched fields keep per-question values.</>}
            </div>
          )}

          {/* Target Exams & Target Classes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={labelStyle}>
              <span>Target Exams {arraysEqual(targetExams, initialTargetExams) && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 400 }}>{mixed.targetExams ? '(Mixed — No Change)' : '(No Change)'}</span>}</span>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '6px 0' }}>
                {PROGRAMME_OPTIONS.map(e => (
                  <label key={e} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={targetExams.includes(e)}
                      onChange={() => setTargetExams(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e])}
                    />
                    {e}
                  </label>
                ))}
              </div>
            </div>
            <div style={labelStyle}>
              <span>Target Classes {arraysEqual(targetClasses, initialTargetClasses) && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 400 }}>{mixed.targetClasses ? '(Mixed — No Change)' : '(No Change)'}</span>}</span>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '6px 0' }}>
                {GRADE_OPTIONS.map(c => (
                  <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={targetClasses.includes(c)}
                      onChange={() => setTargetClasses(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                    />
                    Class {c}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {(!arraysEqual(targetExams, initialTargetExams) || !arraysEqual(targetClasses, initialTargetClasses)) ? (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: -6 }}>
              Changed values will override every question in this import.
            </div>
          ) : (mixed.targetExams || mixed.targetClasses) ? (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: -6 }}>
              Questions have different values for {[mixed.targetExams && 'target exams', mixed.targetClasses && 'target classes'].filter(Boolean).join(' and ')}. Select values to override all, or leave unchanged to keep per-question values.
            </div>
          ) : null}

          {/* Tag */}
          <label style={labelStyle}>
            Tag (optional)
            <input type="text" value={importTag} onChange={(e) => setImportTag(e.target.value)} placeholder="e.g. jee-2024-jan" maxLength={30} style={inputStyle} />
          </label>

          {/* Retain importBatchId toggle */}
          {supportsDryRun && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={retainImportBatchId} onChange={(e) => setRetainImportBatchId(e.target.checked)} />
              Retain existing importBatchId on updates
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>(new questions always get the new batch ID)</span>
            </label>
          )}

          {/* On behalf of — admin only */}
          {teacherOptions && (
            <label style={labelStyle}>
              On behalf of (teacher)
              <select value={onBehalfOf} onChange={(e) => setOnBehalfOf(e.target.value)} style={inputStyle}>
                <option value="">— Public (no teacher) —</option>
                {teacherOptions.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {onBehalfOf && (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  Questions will be private to this teacher.{importTag ? ' Tag will be created for the teacher.' : ''}
                </span>
              )}
            </label>
          )}

          {/* Info message */}
          {infoMessage && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{infoMessage}</div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn btn-outline btn-md" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-md" onClick={start} disabled={!parsed || parsing}>Import</button>
          </div>
        </div>
        );
      })()}

      {phase === 'running' && progress && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {progress.step}
            {progress.fileName && ` — ${progress.fileName}`}
          </div>
          <div style={{ background: 'var(--border-color, #eee)', borderRadius: 4, overflow: 'hidden', height: 10 }}>
            <div style={{
              width: `${progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%`,
              background: 'var(--color-primary, #3b82f6)',
              height: '100%',
              transition: 'width 160ms ease',
            }} />
          </div>
          {progress.total > 1 && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>File {progress.current} of {progress.total}</div>
          )}
        </div>
      )}

      {phase === 'review' && diffResult && (
        <ReviewPanel
          diffResult={diffResult}
          selectedIds={selectedIds}
          expandedIds={expandedIds}
          activeTab={activeTab}
          updatesPivot={updatesPivot}
          loudConfirmed={loudConfirmed}
          onSetLoudConfirmed={setLoudConfirmed}
          onToggleSelection={toggleSelection}
          onToggleAllInTab={toggleAllInTab}
          onToggleExpand={toggleExpand}
          onSetActiveTab={setActiveTab}
          onSetUpdatesPivot={setUpdatesPivot}
          onBack={() => setPhase('configure')}
          onImport={onReviewImport}
          selectedCount={selectedCount}
        />
      )}

      {phase === 'results' && (
        <ResultsPanel
          totals={totals}
          onClose={onClose}
          onReset={() => setPhase('configure')}
          onCreateAssignment={(() => {
            if (!onCreateAssignment) return undefined;
            // Admin path: dialog is invoked with `teacherOptions` so the user can
            // pick which teacher to act on behalf of. The admin's create-assignment
            // backend requires that teacherId, so we keep the button hidden until
            // a teacher is selected.
            // Teacher path: no `teacherOptions`, the import is implicitly by the
            // authenticated teacher; we pass an empty string for the teacherId
            // arg which the teacher wrapper ignores.
            const adminMode = !!teacherOptions;
            if (adminMode && !onBehalfOf) return undefined;
            return (title) => onCreateAssignment(onBehalfOf ?? '', title, totals.createdQuestionIds);
          })()}
        />
      )}
    </Modal>
  );
}

// ── Review Panel ──────────────────────────────────────────────────────

function ReviewPanel({
  diffResult, selectedIds, expandedIds, activeTab, updatesPivot,
  loudConfirmed, onSetLoudConfirmed,
  onToggleSelection, onToggleAllInTab, onToggleExpand, onSetActiveTab, onSetUpdatesPivot,
  onBack, onImport, selectedCount,
}: {
  diffResult: QuestionDiffResult;
  selectedIds: Set<string>;
  expandedIds: Set<string>;
  activeTab: ReviewTab;
  updatesPivot: 'question' | 'field';
  loudConfirmed: boolean;
  onSetLoudConfirmed: (v: boolean) => void;
  onToggleSelection: (id: string) => void;
  onToggleAllInTab: (items: QuestionDiffItem[]) => void;
  onToggleExpand: (id: string) => void;
  onSetActiveTab: (tab: ReviewTab) => void;
  onSetUpdatesPivot: (pivot: 'question' | 'field') => void;
  onBack: () => void;
  onImport: () => void;
  selectedCount: number;
}) {
  // Split server warnings into loud (require explicit ack) and soft (info-only).
  // Loud warnings carry a `[loud] ` prefix from the backend; strip it for display.
  const allWarnings = diffResult.warnings ?? [];
  const loudWarnings = allWarnings
    .filter(w => w.startsWith('[loud]'))
    .map(w => w.replace(/^\[loud\]\s*/, ''));
  const softWarnings = allWarnings.filter(w => !w.startsWith('[loud]'));
  const requiresLoudAck = loudWarnings.length > 0;
  const unchangedItems = diffResult.unchanged ?? [];
  const errorItems = diffResult.errors ?? [];
  const conflictItems = diffResult.conflicts ?? [];
  const tabs = ([
    { key: 'errors' as const, label: 'Errors', items: errorItems, color: '#dc2626' },
    { key: 'conflicts' as const, label: 'Conflicts', items: conflictItems, color: '#a855f7' },
    { key: 'updates' as const, label: 'Updates', items: diffResult.updates, color: '#f59e0b' },
    { key: 'creates' as const, label: 'New', items: diffResult.creates, color: '#22c55e' },
    { key: 'unchanged' as const, label: 'Unchanged', items: unchangedItems, color: '#64748b' },
    { key: 'skipped' as const, label: 'Skipped', items: diffResult.skipped, color: '#94a3b8' },
  ] satisfies { key: ReviewTab; label: string; items: QuestionDiffItem[]; color: string }[])
    .filter(t => t.items.length > 0);

  const currentItems = activeTab === 'errors' ? errorItems
    : activeTab === 'conflicts' ? conflictItems
    : activeTab === 'updates' ? diffResult.updates
    : activeTab === 'creates' ? diffResult.creates
    : activeTab === 'unchanged' ? unchangedItems
    : diffResult.skipped;

  const allTabSelected = currentItems.length > 0 && currentItems.every(i => selectedIds.has(i.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minHeight: 300, maxHeight: '70vh' }}>
      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 16, padding: '8px 0 12px', fontSize: 13, color: 'var(--text-secondary)', flexShrink: 0 }}>
        {errorItems.length > 0 && (
          <span><strong style={{ color: '#dc2626' }}>{errorItems.length}</strong> errors</span>
        )}
        {conflictItems.length > 0 && (
          <span><strong style={{ color: '#a855f7' }}>{conflictItems.length}</strong> conflicts</span>
        )}
        {diffResult.creates.length > 0 && (
          <span><strong style={{ color: '#22c55e' }}>{diffResult.creates.length}</strong> new</span>
        )}
        {diffResult.updates.length > 0 && (
          <span><strong style={{ color: '#f59e0b' }}>{diffResult.updates.length}</strong> updated</span>
        )}
        {unchangedItems.length > 0 && (
          <span><strong style={{ color: '#64748b' }}>{unchangedItems.length}</strong> unchanged</span>
        )}
        {diffResult.skipped.length > 0 && (
          <span><strong style={{ color: '#94a3b8' }}>{diffResult.skipped.length}</strong> skipped</span>
        )}
      </div>

      {/* Batch-level warnings (loud first, then soft). Loud warnings come
          from the backend prefixed with `[loud]` and need an explicit
          confirmation before the import can proceed. */}
      {(loudWarnings.length > 0 || softWarnings.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10, flexShrink: 0 }}>
          {loudWarnings.map((w, i) => (
            <div
              key={`loud-${i}`}
              style={{
                padding: '8px 12px',
                background: 'rgba(220, 38, 38, 0.10)',
                border: '1px solid rgba(220, 38, 38, 0.4)',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--color-error)',
                lineHeight: 1.45,
              }}
            >
              <strong style={{ marginRight: 6 }}>Confirm needed:</strong>{w}
            </div>
          ))}
          {softWarnings.map((w, i) => (
            <div
              key={`soft-${i}`}
              style={{
                padding: '8px 12px',
                background: 'rgba(245, 158, 11, 0.10)',
                border: '1px solid rgba(245, 158, 11, 0.35)',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--text-secondary)',
                lineHeight: 1.45,
              }}
            >
              {w}
            </div>
          ))}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => onSetActiveTab(tab.key)}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-secondary)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.key ? `2px solid ${tab.color}` : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {tab.label} ({tab.items.length})
          </button>
        ))}
      </div>

      {/* Tab header: pivot toggle (Updates only) + Select All */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', flexShrink: 0 }}>
        {activeTab === 'updates' && diffResult.updates.length > 0 ? (
          <div style={{ display: 'inline-flex', border: '1px solid var(--border-color)', borderRadius: 4, overflow: 'hidden' }}>
            {(['question', 'field'] as const).map(p => (
              <button
                key={p}
                onClick={() => onSetUpdatesPivot(p)}
                style={{
                  padding: '4px 12px',
                  fontSize: 12,
                  fontWeight: updatesPivot === p ? 600 : 400,
                  color: updatesPivot === p ? '#fff' : 'var(--text-secondary)',
                  background: updatesPivot === p ? 'var(--color-primary)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                By {p}
              </button>
            ))}
          </div>
        ) : <span />}
        {currentItems.length > 1 && !(activeTab === 'updates' && updatesPivot === 'field') && (
          <button
            onClick={() => onToggleAllInTab(currentItems)}
            style={{ fontSize: 12, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            {allTabSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {/* Question list (or field-pivot view for Updates) */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {activeTab === 'updates' && updatesPivot === 'field' ? (
          <FieldPivotView updates={diffResult.updates} />
        ) : (
          <>
            {currentItems.map(item => (
              <DiffItemCard
                key={item.id}
                item={item}
                checked={selectedIds.has(item.id)}
                expanded={expandedIds.has(item.id)}
                onToggleCheck={() => onToggleSelection(item.id)}
                onToggleExpand={() => onToggleExpand(item.id)}
              />
            ))}
            {currentItems.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                No items in this category.
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--border-color)', flexShrink: 0, flexWrap: 'wrap' }}>
        {errorItems.length > 0 && (
          <span style={{ fontSize: 12, color: '#dc2626', marginRight: 'auto' }}>
            {errorItems.length} error{errorItems.length === 1 ? ' will be' : 's will be'} skipped; valid selections will still import.
          </span>
        )}
        {requiresLoudAck && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-error)', marginRight: errorItems.length > 0 ? 0 : 'auto', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={loudConfirmed}
              onChange={(e) => onSetLoudConfirmed(e.target.checked)}
            />
            I have reviewed the warning above and want to proceed.
          </label>
        )}
        <button className="btn btn-outline btn-md" onClick={onBack}>Back</button>
        <button
          className="btn btn-primary btn-md"
          onClick={onImport}
          disabled={selectedCount === 0 || (requiresLoudAck && !loudConfirmed)}
        >
          Import {selectedCount} selected
        </button>
      </div>
    </div>
  );
}

// ── Field Pivot View ──────────────────────────────────────────────────

/** Pivots the Updates list by field name: shows each changed field with the
 *  list of questions that field changes on. Read-only; selection happens in
 *  the per-question view. */
function FieldPivotView({ updates }: { updates: QuestionDiffItem[] }) {
  const byField = new Map<string, QuestionDiffItem[]>();
  for (const item of updates) {
    for (const field of item.changedFields ?? []) {
      const list = byField.get(field) ?? [];
      list.push(item);
      byField.set(field, list);
    }
  }
  const rows = Array.from(byField.entries())
    .map(([field, items]) => ({ field, items }))
    .sort((a, b) => b.items.length - a.items.length || a.field.localeCompare(b.field));

  if (rows.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
        No field-level changes detected.
      </div>
    );
  }

  return (
    <>
      {rows.map(row => (
        <FieldPivotRow key={row.field} field={row.field} items={row.items} />
      ))}
    </>
  );
}

function FieldPivotRow({ field, items }: { field: string; items: QuestionDiffItem[] }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: 6 }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
          width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12, width: 12 }}>{expanded ? '▾' : '▸'}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {field}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {items.length} question{items.length === 1 ? '' : 's'}
        </span>
      </button>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border-color)', padding: '6px 12px 10px' }}>
          {items.map(item => {
            const text = (item.incomingValue?.questionText ?? item.currentValue?.questionText) as string | undefined;
            const trimmed = text ? text.replace(/\s+/g, ' ').trim().slice(0, 80) : '(no text)';
            return (
              <div key={item.id} style={{ display: 'flex', gap: 10, padding: '4px 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)', flexShrink: 0 }}>
                  {item.id.slice(0, 8)}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {trimmed}{text && text.length > 80 ? '…' : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Diff Item Card ────────────────────────────────────────────────────

function DiffItemCard({ item, checked, expanded, onToggleCheck, onToggleExpand }: {
  item: QuestionDiffItem;
  checked: boolean;
  expanded: boolean;
  onToggleCheck: () => void;
  onToggleExpand: () => void;
}) {
  const badgeStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, flexShrink: 0,
  };

  const badge = item.action === 'create'
    ? { text: 'NEW', bg: '#dcfce7', color: '#166534' }
    : item.action === 'update'
    ? { text: 'UPDATE', bg: '#fef3c7', color: '#92400e' }
    : item.action === 'conflict'
    ? { text: 'CONFLICT', bg: '#f3e8ff', color: '#6b21a8' }
    : item.action === 'unchanged'
    ? { text: 'UNCHANGED', bg: '#e2e8f0', color: '#334155' }
    : item.action === 'error'
    ? { text: 'ERROR', bg: '#fee2e2', color: '#991b1b' }
    : { text: 'SKIPPED', bg: '#f1f5f9', color: '#475569' };

  const hasVersionInfo = item.currentVersion != null && item.incomingVersion != null;
  // Conflict cards diff the incoming question against the existing colliding
  // one, same as updates — so the reviewer can see how they differ.
  const hasDiff = item.action === 'update' || item.action === 'skip' || item.action === 'conflict';

  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: 6 }}>
      {/* Header row */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: item.action === 'error' ? 'default' : 'pointer' }}
        onClick={item.action === 'error' ? undefined : onToggleExpand}
      >
        {item.action !== 'error' && (
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => { e.stopPropagation(); onToggleCheck(); }}
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }}
          />
        )}
        <span style={{ ...badgeStyle, background: badge.bg, color: badge.color }}>{badge.text}</span>
        {item.currentIsLive && (
          <span
            style={{ ...badgeStyle, background: '#fee2e2', color: '#991b1b' }}
            title="Existing question is published (live). Importing will overwrite it."
          >
            LIVE
          </span>
        )}
        <span style={{ fontSize: 13, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.label}
        </span>
        {hasVersionInfo && (
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>
            v{item.currentVersion} → v{item.incomingVersion}
            {item.incomingVersion! < item.currentVersion! && (
              <span style={{ color: '#dc2626', marginLeft: 4 }} title="Incoming version is older than current">⚠ stale</span>
            )}
          </span>
        )}
        {item.action === 'skip' && item.skipReason && (
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>{item.skipReason}</span>
        )}
        {item.action !== 'error' && (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }}>
            {expanded ? '▼' : '▶'}
          </span>
        )}
      </div>

      {item.action === 'error' && item.skipReason && (
        <div style={{ padding: '0 12px 10px 42px', fontSize: 12, color: '#991b1b', whiteSpace: 'pre-wrap' }}>
          {item.skipReason}
        </div>
      )}

      {/* Conflict: list the existing question id(s) this incoming one collides
          with (same exam appearance) so the admin can investigate before
          choosing to create it as new. Check the box to create-as-new. */}
      {item.action === 'conflict' && (item.conflictWithIds?.length ?? 0) > 0 && (
        <div style={{ padding: '0 12px 10px 42px', fontSize: 12, color: 'var(--text-secondary)' }}>
          <span style={{ color: '#6b21a8', fontWeight: 600 }}>Same exam appearance as existing: </span>
          {item.conflictWithIds!.map((cid, i) => (
            <span key={cid}>
              {i > 0 ? ', ' : ''}
              <code style={{ fontSize: 11, background: 'var(--bg-tertiary)', padding: '1px 5px', borderRadius: 3, userSelect: 'all' }}>{cid}</code>
            </span>
          ))}
          <div style={{ marginTop: 4, color: 'var(--text-tertiary)' }}>
            Check this item to import it as a new question; leave unchecked to skip it.
          </div>
        </div>
      )}

      {/* Diff summary */}
      {item.diffSummary && item.diffSummary !== 'No changes' && !expanded && (
        <div style={{ padding: '0 12px 8px 42px', fontSize: 12, color: 'var(--text-tertiary)' }}>
          {item.diffSummary}
        </div>
      )}

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}>
          {hasDiff && item.currentValue && item.incomingValue ? (
            <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 8 }}>
              <JsonDiffViewer
                before={item.currentValue}
                after={item.incomingValue}
                ignoreFields={QUESTION_DIFF_IGNORE}
                title={
                  // For updates the id is identical on both sides, so it never
                  // appears as a diff row — surface it here. For conflicts the
                  // id differs and shows in the field-by-field diff itself.
                  item.action === 'update' ? (
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                      ID:{' '}
                      <code style={{ fontSize: 11, background: 'var(--bg-tertiary)', padding: '1px 5px', borderRadius: 3, userSelect: 'all' }}>{item.id}</code>
                    </span>
                  ) : undefined
                }
              />
            </div>
          ) : item.incomingValue ? (
            <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 8 }}>
              <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'rgba(34, 197, 94, 0.06)', padding: 10, borderRadius: 4 }}>
                {JSON.stringify(item.incomingValue, null, 2)}
              </pre>
            </div>
          ) : (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--text-tertiary)' }}>No data available.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Results ─────────────────────────────────────────────────────────

function ResultsPanel({ totals, onClose, onReset, onCreateAssignment }: {
  totals: Totals;
  onClose: () => void;
  onReset: () => void;
  onCreateAssignment?: (title: string) => Promise<void>;
}) {
  const hasErrors = totals.errors.length > 0;
  const hasWarnings = totals.warnings.length > 0;

  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [assignmentTitle, setAssignmentTitle] = useState('');
  const [assignmentCreating, setAssignmentCreating] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<'success' | 'error' | null>(null);
  const [assignmentError, setAssignmentError] = useState('');

  const canCreateAssignment = onCreateAssignment && totals.createdQuestionIds.length > 0;

  const handleCreateAssignment = async () => {
    if (!onCreateAssignment || !assignmentTitle.trim()) return;
    setAssignmentCreating(true);
    setAssignmentResult(null);
    try {
      await onCreateAssignment(assignmentTitle.trim());
      setAssignmentResult('success');
    } catch (err) {
      setAssignmentResult('error');
      setAssignmentError(err instanceof Error ? err.message : String(err));
    } finally {
      setAssignmentCreating(false);
    }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 10px', borderRadius: 6, fontSize: 13, height: 40, border: '1px solid var(--border-color-dark)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', flex: 1 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {totals.imported > 0 ? (
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          Imported {totals.imported} question{totals.imported === 1 ? '' : 's'}.
        </div>
      ) : hasErrors ? (
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-error)' }}>
          Import failed — no questions written.
        </div>
      ) : (
        <div style={{ fontSize: 14, fontWeight: 600 }}>Nothing to import.</div>
      )}

      <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px' }}>
        <span>Created</span><span>{totals.created}</span>
        <span>Updated</span><span>{totals.updated}</span>
        {totals.unchanged > 0 && (<><span>Unchanged</span><span>{totals.unchanged}</span></>)}
        <span>Skipped</span><span>{totals.skipped}</span>
        {totals.failed > 0 && (<><span>Failed</span><span>{totals.failed}</span></>)}
      </div>

      {hasWarnings && (
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>{totals.warnings.length} warning{totals.warnings.length === 1 ? '' : 's'}</summary>
          <ul style={{ fontSize: 12, color: 'var(--text-tertiary)', paddingLeft: 20, marginTop: 6, maxHeight: 160, overflowY: 'auto' }}>
            {totals.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </details>
      )}

      {hasErrors && (
        <details open>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--color-error)' }}>{totals.errors.length} error{totals.errors.length === 1 ? '' : 's'}</summary>
          <ul style={{ fontSize: 12, color: 'var(--color-error)', paddingLeft: 20, marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
            {totals.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </details>
      )}

      {/* Questions grouped by importBatchId */}
      {Object.keys(totals.questionsByBatchId).length > 0 ? (
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
            Questions by ImportBatchId ({Object.keys(totals.questionsByBatchId).length} batch{Object.keys(totals.questionsByBatchId).length === 1 ? '' : 'es'})
          </summary>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto' }}>
            {Object.entries(totals.questionsByBatchId).map(([batchId, ids]) => (
              <div key={batchId} style={{ padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace', marginBottom: 4 }}>
                  {batchId} ({ids.length})
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {ids.map(id => <div key={id}>{id}</div>)}
                </div>
              </div>
            ))}
          </div>
        </details>
      ) : totals.batchId ? (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
          ImportBatchId: {totals.batchId}
        </div>
      ) : null}

      {/* Create Assignment — only when importing on behalf of a teacher */}
      {canCreateAssignment && !assignmentResult && (
        !showAssignmentForm ? (
          <button
            className="btn btn-secondary btn-md"
            onClick={() => setShowAssignmentForm(true)}
            style={{ alignSelf: 'flex-start' }}
          >
            Create Assignment ({totals.createdQuestionIds.length} questions)
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Create an assignment with the {totals.createdQuestionIds.length} imported question{totals.createdQuestionIds.length === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                value={assignmentTitle}
                onChange={(e) => setAssignmentTitle(e.target.value)}
                placeholder="Assignment name"
                style={inputStyle}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && assignmentTitle.trim()) handleCreateAssignment(); }}
              />
              <button
                className="btn btn-primary btn-md"
                onClick={handleCreateAssignment}
                disabled={!assignmentTitle.trim() || assignmentCreating}
                style={{ whiteSpace: 'nowrap' }}
              >
                {assignmentCreating ? 'Creating...' : 'Create'}
              </button>
              <button
                className="btn btn-outline btn-md"
                onClick={() => setShowAssignmentForm(false)}
                disabled={assignmentCreating}
              >
                Cancel
              </button>
            </div>
          </div>
        )
      )}

      {assignmentResult === 'success' && (
        <div style={{ padding: '8px 12px', background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
          Assignment "<strong>{assignmentTitle}</strong>" created with {totals.createdQuestionIds.length} question{totals.createdQuestionIds.length === 1 ? '' : 's'}.
        </div>
      )}

      {assignmentResult === 'error' && (
        <div style={{ padding: '8px 12px', background: 'rgba(220, 38, 38, 0.12)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: 6, fontSize: 13, color: 'var(--color-error)' }}>
          Failed to create assignment: {assignmentError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
        <button className="btn btn-outline btn-md" onClick={onReset}>Import Another</button>
        <button className="btn btn-primary btn-md" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
